import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/modules/sanr-api';
import { beginSsoRedirect } from '@/modules/sso-pkce';
import type { SsoConfig } from '@/modules/sanr-api';

// One shared definition for the hook and the click-time resolver below, so
// both observe the SAME cache entry and an in-flight fetch is never
// duplicated. retry: false because resolveSsoConfig is awaited inside the
// Sign In click — default backoff retries would hang the click for seconds
// on an unreachable backend, and the failure outcome (sign-in reported
// unavailable) is recoverable on the next page load.
const ssoConfigQuery = {
  queryKey: ['sso-config'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    fetchApi('GET_SSO_CONFIG', { signal }),
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
} as const;

// Fetched once per app lifetime — the config only changes with a backend
// deploy. Consumers treat undefined as SSO-off, and SSO is the only login,
// so an unreachable endpoint leaves no working sign-in.
export function useSsoConfig(): SsoConfig | undefined {
  return useQuery(ssoConfigQuery).data;
}

// Unlike the hook's render-time snapshot, this WAITS for an in-flight
// discovery, so the first click after a page load starts the redirect
// instead of racing the fetch into the unavailable branch.
export async function resolveSsoConfig(
  queryClient: QueryClient,
): Promise<SsoConfig | undefined> {
  try {
    return await queryClient.ensureQueryData(ssoConfigQuery);
  } catch {
    return undefined;
  }
}

const SSO_CALLBACK_PATH = '/auth/callback';

// The callback route the SSO round trip returns to. Must be pre-registered
// on the SSO OAuth client per environment origin, and must byte-match
// between /oauth/authorize and the exchange body.
export function ssoRedirectUri(): string {
  return `${window.location.origin}${SSO_CALLBACK_PATH}`;
}

// Leaves the app for an SSO URL — the authorize page prepareSsoRedirect
// built, or the end-session URL POST_AUTH_LOGOUT returned. Must stay a
// TOP-LEVEL navigation: the SSO session cookie is httpOnly SameSite=None
// (verified in-browser DevTools), but Safari blocks and Firefox partitions
// third-party cookies regardless of SameSite, so only a top-level GET
// reliably carries it — a background fetch of the same URL cannot be
// trusted to end the SSO session. Its own function, called by the
// consumers rather than inside this module, so both legs are observable
// in jsdom tests (window.location is non-redefinable there, and jsdom
// reports a real assign as an unimplemented navigation).
export function leaveForSso(url: string): void {
  window.location.assign(url);
}

// The public market page is the one route that both hosts a logged-out
// login and must be returned to after the SSO round trip (other login
// start points keep the callback's default landings). Matched at the
// redirect's write site so other pages never stash a return path.
const RETURNABLE_PAGE = /^\/markets\/[^/]+$/;

// prompt 'login' forces the issuer's credentials form — the escape from the
// uninvited-account trap; prompt 'select_account' forces the issuer's
// account chooser so a user signed into several Santiment accounts picks
// one instead of the issuer silently resuming the last-used session (the
// issuer's prompt whitelist lives in the sso-pkce prompt doc). opts.returnTo
// overrides the page-derived return path — for callers not ON the page to
// return to (the failed-silent-switch fallback runs from the callback route
// but must land back where the switch started). No prompt at all leaves
// the issuer to its default — a silent resume of the session it holds —
// which is what the demo-stand reset wants: the SSO side has just signed
// the browser in as exactly one account.
export function prepareSsoRedirect(
  config: Extract<SsoConfig, { enabled: true }>,
  opts: { prompt?: 'login' | 'select_account'; returnTo?: string },
): Promise<string> {
  const { pathname, search } = window.location;
  const returnTo =
    opts.returnTo ??
    (RETURNABLE_PAGE.test(pathname) ? `${pathname}${search}` : undefined);
  return beginSsoRedirect({
    issuer: config.issuer,
    clientId: config.clientId,
    scopes: config.scopes,
    redirectUri: ssoRedirectUri(),
    ...(returnTo !== undefined && { returnTo }),
    ...(opts.prompt !== undefined && { prompt: opts.prompt }),
  });
}
